import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Eye, Clock, User, Check, MessageSquare, Send, X } from 'lucide-react';
import { contactAPI, messagesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const res = await contactAPI.list();
      setMessages(res.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const viewMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      markAsRead(message.id);
    }
  };

  const openReplyModal = () => {
    setReplySubject(`Re: Message de ${selectedMessage.prenom} ${selectedMessage.nom}`);
    setReplyContent('');
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!replyContent.trim()) return;
    
    setSending(true);
    try {
      // Marquer comme répondu
      await contactAPI.markRead(selectedMessage.id);
      
      // Ici on pourrait créer un compte utilisateur temporaire et envoyer via messagerie interne
      // Pour l'instant, on notifie juste que le message a été traité
      toast({ 
        title: 'Réponse notée', 
        description: 'Le message a été marqué comme traité. Pensez à contacter le client.' 
      });
      
      setMessages(messages.map(m => 
        m.id === selectedMessage.id ? { ...m, is_read: true, replied: true } : m
      ));
      setSelectedMessage({ ...selectedMessage, is_read: true, replied: true });
      setShowReplyModal(false);
      setReplyContent('');
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de traiter la réponse', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-messages">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Messages de contact</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-[#6777A1] mt-1">{unreadCount} message(s) non lu(s)</p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-[#2C3C65]">Messages ({messages.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6777A1]">Aucun message</p>
              </div>
            ) : (
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => viewMessage(message)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedMessage?.id === message.id ? 'bg-blue-50' : ''
                    } ${!message.is_read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        message.is_read ? 'bg-gray-400' : 'bg-[#285BBB]'
                      }`}>
                        {message.prenom?.[0]}{message.nom?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`font-medium truncate ${!message.is_read ? 'text-[#2C3C65]' : 'text-gray-600'}`}>
                            {message.prenom} {message.nom}
                          </p>
                          {!message.is_read && (
                            <span className="w-2 h-2 bg-[#285BBB] rounded-full flex-shrink-0"></span>
                          )}
                        </div>
                        <p className="text-sm text-[#6777A1] truncate mt-1">{message.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(message.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md">
            {selectedMessage ? (
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-[#285BBB] rounded-full flex items-center justify-center text-white text-xl font-semibold">
                      {selectedMessage.prenom?.[0]}{selectedMessage.nom?.[0]}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#2C3C65]">
                        {selectedMessage.prenom} {selectedMessage.nom}
                      </h3>
                      <p className="text-[#285BBB]">{selectedMessage.email}</p>
                      {selectedMessage.telephone && (
                        <p className="text-sm text-[#6777A1]">{selectedMessage.telephone}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center text-sm text-gray-400">
                      <Clock className="h-4 w-4 mr-1" />
                      {new Date(selectedMessage.created_at).toLocaleString('fr-FR')}
                    </div>
                    {selectedMessage.is_read && (
                      <span className="inline-flex items-center text-xs text-green-600 mt-1">
                        <Check className="h-3 w-3 mr-1" /> Lu
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <p className="text-[#2C3C65] whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={openReplyModal}
                    className="bg-[#285BBB] hover:bg-[#2C3C65]"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Répondre
                  </Button>
                  <a
                    href={`tel:${selectedMessage.telephone}`}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-[#2C3C65] hover:bg-gray-50 transition-colors"
                  >
                    Appeler
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                  <Mail className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-[#6777A1]">Sélectionnez un message pour le lire</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de réponse */}
      <AnimatePresence>
        {showReplyModal && selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReplyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-bold text-[#2C3C65]">Répondre à {selectedMessage.prenom}</h3>
                <button onClick={() => setShowReplyModal(false)}>
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Sujet</label>
                  <Input
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Message</label>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Écrivez votre réponse..."
                    rows={6}
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    💡 Cette réponse sera notée dans le système. N&apos;oubliez pas de contacter le client 
                    par téléphone ({selectedMessage.telephone}) ou par email ({selectedMessage.email}).
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowReplyModal(false)}>
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyContent.trim()}
                    className="bg-[#285BBB] hover:bg-[#2C3C65]"
                  >
                    {sending ? 'Envoi...' : 'Marquer comme traité'}
                    <Check className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminMessages;
